import type { LegendListRef } from '@legendapp/list/react-native';
import type { FC } from 'react';
import { useMemo, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useGetPerson } from '@/features/api/hooks/participant/useGetPerson';
import { useGetGeoArea } from '@/features/api/hooks/participant-geo-area/useGetGeoArea';
import { MeineRegionBody } from '@/features/geo-area/components/region/MeineRegionBody';
import { MeineRegionHeader } from '@/features/geo-area/components/region/MeineRegionHeader';
import { RegionFilterBar } from '@/features/geo-area/components/region/RegionFilterBar';
import { REGION_LOCATION_BAR_HEIGHT } from '@/features/geo-area/components/region/RegionLocationBar';
import { REGION_PAGE_TABS_HEIGHT } from '@/features/geo-area/components/region/RegionPageTabs';
import {
  RegionTabContext,
  type RegionTabContextValue
} from '@/features/geo-area/components/region/RegionTab';
import {
  RegionTab,
  type RegionFilterChange
} from '@/features/geo-area/components/region/regionTypes';
import { useMeineRegionPostFeed } from '@/features/geo-area/hooks/useMeineRegionPostFeed';
import { GradientBackground } from '@/shared/components/GradientBackground';
import { isNative } from '@/shared/utils/platform';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

const styles = StyleSheet.create({
  safeArea: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 3
  }
});

type Props = {
  tab?: RegionTab;
  onVisit: (params: { tab: RegionTab }) => void;
};

export const MeineRegionScreen: FC<Props> = ({ tab, onVisit }) => {
  const theme = useAppTheme();
  const insets = useSafeAreaInsets();

  const activeTab = tab ?? RegionTab.MINISTRIES;
  const [filter, setFilter] = useState<RegionFilterChange | undefined>();
  const [locationBarHeight, setLocationBarHeight] = useState(
    REGION_LOCATION_BAR_HEIGHT
  );
  const scrollY = useSharedValue(0);

  const topInset = isNative ? insets.top : 0;
  const feedInset = topInset + locationBarHeight + REGION_PAGE_TABS_HEIGHT;

  const { data: person } = useGetPerson();
  const { data: homeGeoArea } = useGetGeoArea(person?.homeAreaId, {
    includeAncestors: true
  });
  const feed = useMeineRegionPostFeed(activeTab, filter, person?.homeAreaId);
  const postFeedRef = useRef<LegendListRef>(null);

  const onTabChange = (tab: RegionTab) => {
    if (tab === activeTab) return;
    scrollY.value = 0;
    postFeedRef.current?.scrollToOffset({ offset: 0, animated: false });
    onVisit({ tab });
  };

  const context = useMemo<RegionTabContextValue>(
    () => ({
      activeTab,
      scrollY,
      feedInset,
      locationBarHeight,
      setLocationBarHeight
    }),
    [activeTab, scrollY, feedInset, locationBarHeight]
  );

  return (
    <RegionTabContext value={context}>
      <GradientBackground
        colors={[theme.colors.surface, theme.colors.secondaryContainer]}
        locations={[0.19, 0.84]}
        testID="meine-region-screen"
      >
        <View
          style={[
            styles.safeArea,
            { height: topInset, backgroundColor: theme.colors.surface },
            { pointerEvents: 'none' }
          ]}
        />

        <MeineRegionHeader
          ancestors={homeGeoArea?.ancestors}
          emergencyPosts={feed.emergencyPosts}
          onTabChange={onTabChange}
        />
        <RegionFilterBar onChange={setFilter} />
        <MeineRegionBody
          feed={feed}
          postFeedRef={postFeedRef}
          rootGeoAreaId={homeGeoArea?.ancestors?.[0]?.id}
        />
      </GradientBackground>
    </RegionTabContext>
  );
};
